/* =====================================================================
   INDUCTION HARDWARE — ENERGY, COMFORT & LIFETIME-COST MODEL
   Pure data + math. No DOM. Exposed as window.IHEnergyModel.
   DATA VINTAGE: GAS-VS-INDUCTION-DATA.md, verified 2026-07-24.
   configure() lets the Liquid section's data-* attributes override every
   rate, efficiency and gas-equivalent price without a deploy.
===================================================================== */
window.IHEnergyModel = (function () {
  'use strict';

  var SOURCES = [
    { key: 'eff', label: 'Cooking efficiency', text: 'Induction transfers ~85% of its energy into the cookware vs roughly a third (32\u201333%) for gas \u2014 up to three times more efficient. U.S. Department of Energy.', url: 'https://www.energy.gov/articles/making-switch-induction-stoves-or-cooktops' },
    { key: 'eff-oven', label: 'Oven efficiency', text: 'Gas oven ~30% vs electric oven ~78% point-of-use, per the DOE residential cooking-products test procedure (10 CFR 430 subpt. B app. I).', url: 'https://www.energy.gov/articles/making-switch-induction-stoves-or-cooktops' },
    { key: 'elec', label: 'Electricity rate', text: 'NV Energy Schedule D-1, effective 1 Apr 2026: $0.11798/kWh tariff, ~$0.124/kWh all-in with the Universal Energy Charge and local fees.', url: 'https://www.nvenergy.com/publish/content/dam/nvenergy/bill_inserts/2026/04_apr/spp_nv_resrates-4_1_30.pdf' },
    { key: 'gas', label: 'Gas rate', text: 'Southwest Gas Schedule NG-RS, effective 1 Jul 2026: $0.939/therm all-in (delivery $0.567 + gas cost $0.706 \u2212 DEAA $0.350 + IE $0.017).', url: 'https://www.swgas.com/nvtariff.pdf' },
    { key: 'fixed', label: 'Gas customer charge', text: 'Southwest Gas NG-RS basic service charge $10.80/mo (~$130/yr) \u2014 billed whether or not you burn a therm, avoidable only on full electrification.', url: 'https://www.swgas.com/nvtariff.pdf' },
    { key: 'peer', label: 'Gas-equivalent price', text: 'Researched street price of the real gas rival at the same width and tier, held per SKU as custom.gas_equiv_price on 42 cooking products.', url: '' },
    { key: 'thermal', label: 'Kitchen air model', text: 'First-order lumped thermal network: air at 0.018 BTU/ft\u00B3\u00B7\u00B0F with 4\u00D7 that in cabinetry and surfaces, 3 air changes/hour of kitchen ventilation at 1.08 BTU/hr\u00B7\u00B0F per CFM, plus 190 BTU/hr\u00B7\u00B0F of conduction and buoyant mixing into adjoining space. Calibrated to the 4\u201310\u00B0F rise reported in residential cooking field measurements.', url: '' },
    { key: 'hood', label: 'Hood capture', text: '60\u201380% of the convective cooktop plume for a ducted residential hood on high. Radiant heat off flame and grates is not capturable at any speed; gas oven flue gases vent into the room outside the hood capture zone.', url: '' },
    { key: 'ac', label: 'Cooling', text: 'Air conditioning removes waste heat at a seasonal COP of 3.3 (about SEER2 15). Cooling-season length is a user input; Reno-Tahoe runs about four months.', url: '' },
    { key: 'life', label: 'Service life', text: 'Residential gas range service life 15\u201320 years (default 18). Out-of-warranty repair averages ~$95/yr from year 9 \u2014 igniters, safety valves, regulators, spark modules. Induction Hardware carries a lifetime parts warranty.', url: '' },
    { key: 'no2', label: 'Indoor air quality', text: 'Replacing a gas or propane stove with electric cuts household nitrogen dioxide exposure by more than 25% on average, and about 50% for households that cook often. Kashtan et al., PNAS Nexus 2025;4(12):pgaf341.', url: 'https://academic.oup.com/pnasnexus/article/4/12/pgaf341/8361964' },
    { key: 'epa', label: 'Indoor NO\u2082', text: 'In homes with gas stoves, indoor nitrogen dioxide levels often exceed outdoor levels; homes without combustion appliances average about half the outdoor level. U.S. EPA.', url: 'https://www.epa.gov/indoor-air-quality-iaq/nitrogen-dioxides-impact-indoor-air-quality' }
  ];

  var K = {
    KWH_PER_THERM: 29.3,
    BTU_PER_KWH: 3412,
    AIR_BTU_PER_FT3_F: 0.018,
    CFM_UA: 1.08
  };

  var PHYS = {
    cooktopGasEff: 0.33,
    cooktopIndEff: 0.85,
    ovenGasEff: 0.30,
    ovenElecEff: 0.78,
    gasConvectiveFrac: 0.70,
    indConvectiveFrac: 0.40,
    ovenCaptureDerate: 0.30,
    thermalMassMult: 4.0,
    interiorUA: 190,
    kitchenACH: 3.0,
    acCOP: 3.3,
    repairsStartYear: 9,
    repairsPerYear: 95,
    no2Reduction: 0.25
  };

  var RATES = {
    nevada:   { id: 'nevada',   label: 'Northern Nevada \u2014 NV Energy D-1 + Southwest Gas NG-RS', elec: 0.124,  gas: 0.939, gasFixed: 10.80 },
    national: { id: 'national', label: 'U.S. average',                                              elec: 0.1883, gas: 1.45,  gasFixed: 18.00 },
    custom:   { id: 'custom',   label: 'From my bill',                                              elec: 0.124,  gas: 0.939, gasFixed: 10.80 }
  };

  var KITCHENS = {
    galley:   { id: 'galley',   label: 'Galley / closed', volume: 1400 },
    standard: { id: 'standard', label: 'Standard kitchen', volume: 2025 },
    open:     { id: 'open',     label: 'Open plan',        volume: 3600 }
  };

  var HOODS = {
    always:    { id: 'always',    label: 'Always, on high', capture: 0.75 },
    sometimes: { id: 'sometimes', label: 'Sometimes',       capture: 0.45 },
    rarely:    { id: 'rarely',    label: 'Rarely',          capture: 0.15 }
  };

  var SKUS = [
    { id: 'S-IR30-PRO', label: '30\u2033 Pro Series Induction Range', family: 'cooktop', useKwh: 0.55, hours: 0.70, gasPeer: 2450,  gasLife: 18, gasLabel: 'a comparable 30\u2033 pro-style gas range' },
    { id: 'S-IR30',     label: '30\u2033 Luxury Series Induction Range', family: 'cooktop', useKwh: 0.55, hours: 0.70, gasPeer: 4400,  gasLife: 18, gasLabel: 'a comparable 30\u2033 pro gas range' },
    { id: 'S-IR36',     label: '36\u2033 Luxury Series Induction Range', family: 'cooktop', useKwh: 0.70, hours: 0.78, gasPeer: 5500,  gasLife: 18, gasLabel: 'a comparable 36\u2033 pro gas range' },
    { id: 'S-IR48',     label: '48\u2033 Luxury Series Induction Range', family: 'cooktop', useKwh: 0.85, hours: 0.85, gasPeer: 7000,  gasLife: 18, gasLabel: 'a comparable 48\u2033 pro gas range' },
    { id: 'S-IR60',     label: '60\u2033 Luxury Series Induction Range', family: 'cooktop', useKwh: 1.00, hours: 0.92, gasPeer: 14000, gasLife: 18, gasLabel: 'a comparable 60\u2033 pro gas range' },
    { id: 'IRT30',      label: '30\u2033 Induction Rangetop',         family: 'cooktop', useKwh: 0.50, hours: 0.68, gasPeer: 1900,  gasLife: 15, gasLabel: 'a comparable 30\u2033 pro gas rangetop' },
    { id: 'IRT36',      label: '36\u2033 Induction Rangetop',         family: 'cooktop', useKwh: 0.55, hours: 0.70, gasPeer: 1800,  gasLife: 15, gasLabel: 'a comparable 36\u2033 pro gas rangetop' },
    { id: 'IRT48',      label: '48\u2033 Induction Rangetop',         family: 'cooktop', useKwh: 0.68, hours: 0.78, gasPeer: 2600,  gasLife: 15, gasLabel: 'a comparable 48\u2033 pro gas rangetop with griddle' },
    { id: 'DI-24',      label: '24\u2033 LED Induction Drop-In Cooktop', family: 'cooktop', useKwh: 0.42, hours: 0.62, gasPeer: 400,  gasLife: 15, gasLabel: 'a comparable 24\u2033 gas drop-in cooktop' },
    { id: 'DI-30',      label: '30\u2033 LED Induction Drop-In Cooktop', family: 'cooktop', useKwh: 0.50, hours: 0.68, gasPeer: 850,  gasLife: 15, gasLabel: 'a comparable 30\u2033 gas drop-in cooktop' },
    { id: 'DI-36',      label: '36\u2033 LED Induction Drop-In Cooktop', family: 'cooktop', useKwh: 0.55, hours: 0.70, gasPeer: 1350, gasLife: 15, gasLabel: 'a comparable 36\u2033 gas drop-in cooktop' },
    { id: 'WO-30',      label: '30\u2033 Single Wall Oven',           family: 'oven',    useKwh: 0.90, hours: 1.20, gasPeer: 2900,  gasLife: 16, gasLabel: 'the closest comparable 30\u2033 gas wall oven' },
    { id: 'WO-D-30',    label: '30\u2033 Double Wall Oven',           family: 'oven',    useKwh: 1.30, hours: 1.30, gasPeer: 3600,  gasLife: 16, gasLabel: 'the closest 30\u2033 gas double-oven alternative' },
    { id: 'BIG36',      label: '36\u2033 Built-In Outdoor Induction Griddle', family: 'cooktop', useKwh: 0.75, hours: 0.80, gasPeer: 2700, gasLife: 12, gasLabel: 'a comparable built-in outdoor gas griddle' }
  ];

  var BY_ID = {};
  SKUS.forEach(function (s) { BY_ID[s.id] = s; });
  function sku(id) { return BY_ID[id] || BY_ID['S-IR48']; }

  function configure(o) {
    if (!o) return;
    ['elecRate', 'gasRate', 'gasFixed'].forEach(function (k) {
      var map = { elecRate: 'elec', gasRate: 'gas', gasFixed: 'gasFixed' };
      if (o[k] > 0) { RATES.nevada[map[k]] = o[k]; RATES.custom[map[k]] = o[k]; }
    });
    Object.keys(PHYS).forEach(function (k) { if (typeof o[k] === 'number' && isFinite(o[k])) PHYS[k] = o[k]; });
  }

  function session(s, hoodId) {
    var isOven = s.family === 'oven';
    var gasEff = isOven ? PHYS.ovenGasEff : PHYS.cooktopGasEff;
    var indEff = isOven ? PHYS.ovenElecEff : PHYS.cooktopIndEff;

    var gasInputKwh = s.useKwh / gasEff;
    var indInputKwh = s.useKwh / indEff;

    var gasBtuHr = gasInputKwh * K.BTU_PER_KWH / s.hours;
    var indBtuHr = indInputKwh * K.BTU_PER_KWH / s.hours;

    var gasWaste = gasBtuHr * (1 - gasEff);
    var indWaste = indBtuHr * (1 - indEff);

    var capture = (HOODS[hoodId] || HOODS.sometimes).capture;
    if (isOven) capture *= PHYS.ovenCaptureDerate;

    var gasRoom = gasWaste * (PHYS.gasConvectiveFrac * (1 - capture) + (1 - PHYS.gasConvectiveFrac));
    var indRoom = indWaste * (PHYS.indConvectiveFrac * (1 - capture) + (1 - PHYS.indConvectiveFrac));

    return {
      isOven: isOven, hours: s.hours, capture: capture,
      gasEff: gasEff, indEff: indEff,
      gasInputKwh: gasInputKwh, indInputKwh: indInputKwh,
      gasTherms: gasInputKwh / K.KWH_PER_THERM,
      gasBtuHr: gasBtuHr, indBtuHr: indBtuHr,
      gasWaste: gasWaste, indWaste: indWaste,
      gasRoom: gasRoom, indRoom: indRoom
    };
  }

  function thermal(opt) {
    var s = sku(opt.skuId);
    var se = session(s, opt.hood);
    var kit = KITCHENS[opt.kitchen] || KITCHENS.standard;
    var baseF = typeof opt.baseF === 'number' ? opt.baseF : 72;

    var C = kit.volume * K.AIR_BTU_PER_FT3_F * PHYS.thermalMassMult;
    var vent = (kit.volume * PHYS.kitchenACH / 60) * K.CFM_UA;
    var UA = vent + PHYS.interiorUA;
    var tau = C / UA;
    var cook = s.hours;

    function riseAt(Q, t) {
      var ss = Q / UA;
      if (t <= cook) return ss * (1 - Math.exp(-t / tau));
      var peak = ss * (1 - Math.exp(-cook / tau));
      return peak * Math.exp(-(t - cook) / tau);
    }

    var spanH = cook + 0.75;
    var N = 96, gasPts = [], indPts = [], i, t;
    for (i = 0; i <= N; i++) {
      t = spanH * i / N;
      gasPts.push({ h: t, f: baseF + riseAt(se.gasRoom, t) });
      indPts.push({ h: t, f: baseF + riseAt(se.indRoom, t) });
    }

    var minutes = typeof opt.minutes === 'number' ? opt.minutes : cook * 60;
    minutes = Math.max(0, Math.min(spanH * 60, minutes));
    var tNow = minutes / 60;

    return {
      sku: s, session: se, kitchen: kit,
      baseF: baseF, tauMin: tau * 60, UA: UA, C: C,
      ventCfm: kit.volume * PHYS.kitchenACH / 60,
      cookMinutes: cook * 60, spanMinutes: spanH * 60, minutes: minutes,
      gasCurve: gasPts, indCurve: indPts,
      gasNowF: baseF + riseAt(se.gasRoom, tNow),
      indNowF: baseF + riseAt(se.indRoom, tNow),
      gasPeakF: baseF + riseAt(se.gasRoom, cook),
      indPeakF: baseF + riseAt(se.indRoom, cook)
    };
  }

  function resolveRates(opt) {
    var base = RATES[opt.rateId] || RATES.nevada;
    if (opt.rateId !== 'custom') return base;
    return {
      id: 'custom', label: RATES.custom.label,
      elec: opt.elec > 0 ? opt.elec : base.elec,
      gas: opt.gas > 0 ? opt.gas : base.gas,
      gasFixed: opt.gasFixed >= 0 ? opt.gasFixed : base.gasFixed
    };
  }

  function annual(opt) {
    var s = sku(opt.skuId);
    var se = session(s, opt.hood);
    var r = resolveRates(opt);
    var sessions = (opt.sessionsPerWeek || 10) * 52;
    var coolingFrac = Math.max(0, Math.min(1, (opt.acMonths != null ? opt.acMonths : 4) / 12));

    var gasFuel = sessions * se.gasTherms * r.gas;
    var indFuel = sessions * se.indInputKwh * r.elec;

    var acBtu = sessions * (se.gasRoom - se.indRoom) * se.hours * coolingFrac;
    var acKwh = acBtu / K.BTU_PER_KWH / PHYS.acCOP;

    return {
      rates: r, sessions: sessions, coolingFrac: coolingFrac,
      gasTherms: sessions * se.gasTherms,
      indKwh: sessions * se.indInputKwh,
      gasFuel: gasFuel, indFuel: indFuel,
      fuelDelta: gasFuel - indFuel,
      acBtu: acBtu, acKwh: acKwh, acCost: acKwh * r.elec,
      gasFixedYear: r.gasFixed * 12,
      session: se, sku: s
    };
  }

  function lifetime(opt) {
    var a = annual(opt);
    var s = a.sku;
    var peer = opt.gasPeer > 0 ? opt.gasPeer : s.gasPeer;
    var life = opt.gasLife > 0 ? opt.gasLife : s.gasLife;
    var peerLabel = opt.gasLabel || s.gasLabel;
    var years = Math.max(1, Math.round(opt.years || 20));
    var closeAccount = !!opt.closeAccount;

    var gasSeries = [0], indSeries = [0];
    var gasCum = 0, indCum = 0, crossover = null;
    var totals = { fuel: 0, ac: 0, fixed: 0, repairs: 0, replace: 0 };

    for (var y = 1; y <= years; y++) {
      var gasYear = a.gasFuel + a.acCost;
      var indYear = a.indFuel;
      totals.fuel += a.fuelDelta;
      totals.ac += a.acCost;

      if (closeAccount) { gasYear += a.gasFixedYear; totals.fixed += a.gasFixedYear; }
      if (y >= PHYS.repairsStartYear) { gasYear += PHYS.repairsPerYear; totals.repairs += PHYS.repairsPerYear; }
      if (y % life === 0) { gasYear += peer; totals.replace += peer; }

      gasCum += gasYear; indCum += indYear;
      gasSeries.push(gasCum); indSeries.push(indCum);
      if (crossover === null && gasCum - indCum > 0) crossover = y;
    }

    return {
      annual: a, sku: s, years: years, closeAccount: closeAccount,
      gasPeer: peer, gasLife: life, gasLabel: peerLabel,
      gasSeries: gasSeries, indSeries: indSeries,
      gasTotal: gasCum, indTotal: indCum, net: gasCum - indCum,
      crossover: crossover,
      replacements: Math.floor(years / life),
      rows: totals
    };
  }

  return {
    K: K, PHYS: PHYS, RATES: RATES, KITCHENS: KITCHENS, HOODS: HOODS,
    SKUS: SKUS, SOURCES: SOURCES,
    configure: configure, sku: sku, session: session,
    thermal: thermal, annual: annual, lifetime: lifetime
  };
})();